import { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import { MARKETPLACE_CONFIG } from '@com/core/common/utils/config';

import {
    TCatalog,
    TCatalogSuggestions,
    TProductDetail,
    TRecommendationsResponse,
    TCategoryResponse,
    TPopularBrands,
    TCatalogResponse,
} from '../types/catalog';
import { TLocaleAPI } from '../types/locale';

/**
 * Endpoint for catalog
 */

export type TCatalogEndpoints = {
    getAllBrands: () => Promise<AxiosResponse<TCatalog>>;

    getCatalogData: (path: string, config?: AxiosRequestConfig) => Promise<AxiosResponse<TCatalog>>;

    getCategories: () => Promise<AxiosResponse<TCatalog>>;

    getSearch: (query: Record<string, string>) => Promise<AxiosResponse<TCatalog>>;

    getSearchSuggestions: (query: string, store?: string) => Promise<AxiosResponse<TCatalogSuggestions>>;

    post: (query: Record<string, string>) => Promise<AxiosResponse<TCatalog>>;

    getPopularBrands: (criteria: string) => Promise<AxiosResponse<TPopularBrands>>;

    getProductSizeGuideBySku: (sku: string) => Promise<AxiosResponse<string>>;

    getProductDetailsForSkus: (skus: string) => Promise<AxiosResponse<TCatalog>>;

    getProduct: (sku: string, offer?: string) => Promise<AxiosResponse<TProductDetail>>; // #TODO, needs improvement. added for bulk app

    getRecommendations: (
        event: string,
        ingest: Record<string, string>,
    ) => Promise<AxiosResponse<TRecommendationsResponse>>;

    getCategoryData: () => Promise<AxiosResponse<TCategoryResponse>>;
};

const SVC_ENDPOINT = MARKETPLACE_CONFIG.endpoints.catalog;
const SVC_ENDPOINT_CATALOG = `${SVC_ENDPOINT}/u`;
const SVC_ENDPOINT_SIZING_GUIDE = `/_svc/sizing-guide`;
const SVC_ENDPOINT_RECOMMENDATIONS = `${SVC_ENDPOINT}/recapi`;

export default (api: AxiosInstance, apiLocale: TLocaleAPI): TCatalogEndpoints => {
    const getAllBrands = async () =>
        api.get<TCatalog>(`${SVC_ENDPOINT_CATALOG}/allbrands`, { headers: { 'x-Locale': apiLocale } });

    const getCatalogData = async (path: string) => {
        const { headers } = api.defaults.headers;
        return api.get<TCatalog>(
            // Get rid of undefined elements
            // Get rid of double slashes
            `${[SVC_ENDPOINT_CATALOG, path]
                .filter(v => v)
                .join('/')
                .replace(/\/\/+/g, '/')}`,
            { headers: { ...headers, 'x-Locale': apiLocale } },
        );
    };

    const getCategories = async () =>
        api.get<TCatalog>(`${SVC_ENDPOINT_CATALOG}/_data/2017/mobile-nav/v2`, {
            headers: { 'x-Locale': apiLocale },
        });

    const getSearch = async (query: Record<string, string>) =>
        api.get<TCatalog>(`${SVC_ENDPOINT_CATALOG}/search/`, {
            params: query,
            headers: { 'x-Locale': apiLocale },
        });

    const post = async (query: Record<string, string>) =>
        api.post<TCatalog>(`${SVC_ENDPOINT}/search`, query, {
            headers: { 'x-Locale': apiLocale },
        });

    const getPopularBrands = async (criteria: string) =>
        api.get<TPopularBrands>(`${SVC_ENDPOINT_CATALOG}/brands/${criteria}`, { headers: { 'x-Locale': apiLocale } });

    const getSearchSuggestions = async (query: string, store?: string) =>
        api.get<TCatalogSuggestions>(
            `${SVC_ENDPOINT}/suggestions?q=${encodeURIComponent(query)}${store ? `&store=${store}` : ''}`,
            { headers: { 'x-Locale': apiLocale } },
        );

    const getProductSizeGuideBySku = async (sku: string) =>
        api.get<string>(`${SVC_ENDPOINT_SIZING_GUIDE}/${sku}`, { headers: { 'x-Locale': apiLocale } });

    const getProductDetailsForSkus = async (skus: string) =>
        api.get<TCatalog>(`${SVC_ENDPOINT}/search?sku=${skus}`, {
            headers: { 'x-Locale': apiLocale },
        });

    const getProduct = async (sku: string) =>
        api.get<TProductDetail>(`${SVC_ENDPOINT_CATALOG}/${sku}/p`, {
            headers: { 'x-Locale': apiLocale },
        });

    const getRecommendations = async (event: string, params: Record<string, string>) =>
        api.post<TRecommendationsResponse>(`${SVC_ENDPOINT_RECOMMENDATIONS}/ingest/${event}`, params, {
            headers: { 'x-Locale': apiLocale },
        });

    const getCategoryData = async () =>
        api.get<TCategoryResponse>(`${SVC_ENDPOINT_CATALOG}/_data/2017/mobile-nav/v2?x-content=mobile`, {
            headers: { 'x-Locale': apiLocale },
        });

    return {
        getAllBrands,
        getCatalogData,
        getCategories,
        getSearch,
        getSearchSuggestions,
        post,
        getPopularBrands,
        getProductSizeGuideBySku,
        getProductDetailsForSkus,
        getProduct,
        getRecommendations,
        getCategoryData,
    };
};

export type TInternalCatalogEndpoints = {
    getCatalogData: (path: string, config?: AxiosRequestConfig) => Promise<AxiosResponse<TCatalogResponse>>;
    getCategories: () => Promise<AxiosResponse<TCatalog>>;
    getCategoryData: () => Promise<AxiosResponse<TCategoryResponse>>;
};

export const internalCatalog = (api: AxiosInstance, apiLocale: TLocaleAPI): TInternalCatalogEndpoints => {
    const getCatalogData = async (path: string, config?: AxiosRequestConfig) =>
        api.get<TCatalogResponse>(
            // Get rid of undefined elements
            // Get rid of double slashes
            `${['', path] // set in baseConfig
                .filter(v => v)
                .join('/')
                .replace(/\/\/+/g, '/')}`,
            { ...config, headers: { ...config.headers, 'x-Locale': apiLocale } },
        );

    const getCategories = async () =>
        api.get<TCatalog>(`/_data/2017/mobile-nav/v2`, { headers: { 'x-Locale': apiLocale } });

    const getCategoryData = async () =>
        api.get<TCategoryResponse>(`/_data/2017/mobile-nav/v2?x-content=mobile`, {
            headers: { 'x-Locale': apiLocale },
        });

    return {
        getCatalogData,
        getCategories,
        getCategoryData,
    };
};
